// Enums
export enum ProductStatus {
  PENDING = "pending",
  APPROVED = "approved",
  REJECTED = "rejected",
  ON_HOLD = "on-hold",
  DRAFT = "draft",
}

export enum ProductCondition {
  NEW = "new",
  REFURBISHED = "refurbished",
}

export enum ProductCountry {
  INDIA = "India",
  GLOBAL = "Global",
}

export enum SortBy {
  CREATED_AT = "createdAt",
  UPDATED_AT = "updatedAt",
  NAME = "name",
  PRICE = "price",
}

export enum SortOrder {
  ASC = "asc",
  DESC = "desc",
}

export enum DiscountType {
  AMOUNT_OFF = "AMOUNT_OFF",
  PERCENT_OFF = "PERCENT_OFF",
}

export enum DealType {
  LIGHTNING_DEAL = "LIGHTNING_DEAL",
  SEVEN_DAY_DEAL = "SEVEN_DAY_DEAL",
}

export enum PromotionType {
  PERCENT_OFF = "PERCENT_OFF",
  BUY_X_GET_Y_FREE = "BUY_X_GET_Y_FREE",
}

export enum TargetAudience {
  ALL_CUSTOMERS = "ALL_CUSTOMERS",
  AURA_CUSTOMERS = "AURA_CUSTOMERS",
}

// Product Interfaces
export interface ProductPricing {
  unitPrice: number;
  offerPrice?: number;
  finalPrice: number;
  gsttype: string | any;
  displayGSTamount?: number;
}

export interface ProductOffers {
  salarShoppingVouchers?: string[];
  salarShoppingVoucherAmount?: number;
  salarShoppingVoucherValidity?: number;
  autoSalarShoppingVoucherAmount?: number;
  autoSalarShoppingVoucherValidity?: number;
}

export interface ProductCommission {
  customerSponsorCommission: number;
  sellerReferralCommission: number;
  companyReferralFee?: number;
}

export interface PurchaseLimits {
  minimumPurchaseLimits: number;
  maximumPurchaseLimits: number;
}

export interface ProductDimensions {
  length: number;
  lengthUnit: string;
  width: number;
  widthUnit: string;
  height: number;
  heightUnit: string;
  weight: number;
  weightUnit: string;
}

export interface SaleDates {
  start: Date | string;
  end: Date | string;
}

export interface ProductMedia {
  images?: string[];
  videos?: string[];
}

export interface ProductVariation {
  sku: string;
  ssin: string;
  attributes?: Record<string, any>;
  qty: number;
  itemCondition: "new" | "refurbished";
  pricing: ProductPricing;
  offers?: ProductOffers;
  commissions?: ProductCommission;
  purchaseLimits?: PurchaseLimits;
  saleDates: SaleDates;
  itemDimensions: ProductDimensions;
  itemPackageDimensions: ProductDimensions;
  media: ProductMedia;
}

export interface ProductCategoryHierarchy {
  superCategory: string;
  category: string;
  subCategory: string;
  childCategory: string;
  categoryId?: string;
}

export interface ProductStock {
  availableStock: number;
  warningStock?: number;
  lowStockWarning?: boolean;
  stockDisplayinwebsite?: boolean;
}

export interface ProductVendors {
  manufacturedBy: string;
  marketedBy: string;
}

export interface ProductMetaDetails {
  keywords: string;
  metaDescription?: string;
}

export interface ProductPerformance {
  sales?: number;
  unitsSold?: number;
  pageViews?: number;
  salesRank?: number;
}

export interface ProductReviews {
  averageRating?: number;
  totalReviews?: number;
}

export interface DayDelivery {
  status: boolean;
  district: string[];
}

export interface FAQ {
  question: string;
  answer: string;
}

export interface BoughtTogether {
  productIds: Product[] | null;
  discountPercentage: number;
}

export interface AppliedOfferSnapshot {
  type?: string; // DEAL | PROMOTION | COUPON | BOGO
  fundedBy?: string; // SELLER | BRAND | PLATFORM
  amount?: number;
}

export interface FeaturedOfferSnapshot {
  sellerSkuId?: string;
  sellerId?: string;
  sellerName?: string;
  finalPrice?: number;
  unitPrice?: number;
  discountPercent?: number;
  availableQty?: number;
  freeDelivery?: boolean;
  fastDelivery?: boolean;
  nextDayDelivery?: boolean;
  sellerRating?: number;
  appliedOffers?: AppliedOfferSnapshot[];
  computedAt?: Date | string;
}

export interface CatalogScoreBreakdown {
  content: number;
  media: number;
  category: number;
  reviews: number;
  sellers: number;
}

export interface CatalogScore {
  score: number;
  breakdown: CatalogScoreBreakdown;
  issues: string[];
  lastCalculatedAt: Date | string;
}

export interface Product {
  _id: string;
  name: string;
  mainImage: string;
  images?: string[];
  brandId: string | any;
  productTypeId?: string | any;
  gstId?: string | any;
  ssin: string;
  sku?: string;
  productId?: string;
  description: string;
  bulletPoints?: string[];
  barcode?: string;
  categoryHierarchy: ProductCategoryHierarchy;
  condition: "new" | "refurbished";
  country: "India" | "Global";
  attributes?: Record<string, any>;
  stock?: ProductStock;
  variations?: ProductVariation[];
  vendors?: ProductVendors;
  seo?: SeoDetails;
  metaDetails?: ProductMetaDetails[];
  boughtTogether?: BoughtTogether;
  frequentlyBought?: Product[];
  productReleaseDate?: Date | string;
  performance?: ProductPerformance;
  reviews?: ProductReviews;
  status?: ProductStatus;
  moderationStatus?: "pending" | "approved" | "rejected" | "on-hold" | "draft" | ProductStatus;
  isDeleted?: boolean;
  createdBy: "ADMIN" | "SELLER" | "STAFF";
  creatorId?: string;
  sellerId?: string;
  sellerData?: SellerData;
  freeDelivery?: boolean;
  returnAndReplace?: boolean;
  daysToReturnAndReplace?: number;
  sameDayDelivery?: DayDelivery;
  nextDayDelivery?: DayDelivery;
  faqs?: FAQ[];
  variationTheme?: string[];
  productCopy?: {
    isProductCopy: boolean;
    originalProductSKU: string;
    creatorId: string;
    originalProductSsin: string;
  };
  catalogScore?: CatalogScore;
  featuredOffer?: FeaturedOfferSnapshot;
  variantCount?: number;
  sellerCount?: number;
  source?: "new_flow" | "migrated" | string;
  version?: number;
  approvedAt?: Date | string;
  approvedBy?: string;
  rejectionReason?: string;
  rejectedAt?: Date | string;
  rejectedBy?: string;
  onHoldReason?: string;
  onHoldAt?: Date | string;
  onHoldBy?: string;
  moderatedBy?: string;
  moderatedAt?: Date | string;
  createdAt?: Date | string;
  updatedAt?: Date | string;
  // Extended fields for products with deals/coupons/promotions
  coupons?: Coupon[];
  deals?: Deal[];
  promotions?: Promotion[];
  auraDiscounts?: AuraExclusiveDiscount[];
  otherSellers?: OtherSeller[];
}

// Seller Data Interface
export interface SellerData {
  _id: string;
  sellerId: string;
  businessInfo?: {
    businessName?: string;
    companyName?: string;
    mobileNo?: string;
    emailId?: string;
    dateOfIncorporation?: string;
    segment?: string;
    subSegment?: string;
    ownershipType?: string;
    country?: string;
    state?: string;
    district?: string;
    city?: string;
    addressLine1?: string;
    addressLine2?: string;
    landmark?: string;
    pincode?: string;
    department?: string;
  };
  businessIndividual?: {
    name?: string;
    mobileNo?: string;
    emailId?: string;
  };
  personalDetails?: {
    name?: string;
    designation?: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

// Other Seller Interfaces
export interface OtherSellerInventory {
  _id: string;
  sellerSkuId: string;
  sellerId: string;
  availableQty: number;
  reservedQty: number;
  damagedQty: number;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface OtherSellerVariation {
  _id: string;
  listingId: string;
  sellerId: string;
  variantId: string;
  sellerSku: string;
  fulfillmentSku: string;
  createdAt: string;
  updatedAt: string;
  inventory: OtherSellerInventory;
  pricing?: ProductPricing;
}

export interface OtherSeller {
  _id: string;
  catalogProductId: string;
  sellerId: string;
  listingStatus: string;
  sellerSpecificMedia: string[];
  createdAt: string;
  updatedAt: string;
  variations: OtherSellerVariation[];
}

export interface SeoDetails {
  metaTitle?: string;
  keywords: string;
  metaDescription: string;
  _id?: string;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

export interface Brand {
  _id: string;
  brandID: string;
  name: string;
  image?: string;
  banner?: string[];
  description: string;
  slug?: string;
  showOnWebsite: boolean;
  showInFilters: boolean;
  showOnProductDetails: boolean;
  showInSlider: boolean;
  brandpage: boolean;
  seoDetails?: SeoDetails;
  status: boolean;
  displayOrder: number;
  isDeleted: boolean;
  topBrand: boolean;
  staffdetails?: any;
  createdAt?: string;
  updatedAt?: string;
}

export interface BrandWithProducts extends Brand {
  products: Product[];
}

// Coupon Interfaces
export interface CouponRedemptionHistory {
  userId: string;
  ssin: string;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

export interface CouponAnalytics {
  totalRedemptions: number;
  lastRedemptionDate: Date | string;
  totalDiscountGiven: number;
  totalCoupons: number;
  couponLeft: number;
  couponAmount: number;
  totalRedemptionsUser: CouponRedemptionHistory[];
}

export interface CouponConditions {
  minimumOrderApply?: boolean;
  minimumQuantity?: number;
}

export interface Coupon {
  _id: string;
  couponId: string;
  title: string;
  productId: string;
  ssinList: string[];
  discountType: "AMOUNT_OFF" | "PERCENT_OFF";
  discountValue: number;
  budget: number;
  startDate: Date | string;
  endDate: Date | string;
  targetAudience: "ALL_CUSTOMERS" | "AURA_CUSTOMERS";
  status: "ACTIVE" | "PENDING" | "ENDED" | "PAUSED" | "RESUMED";
  feePerRedemption: number;
  visibility: boolean;
  createdBy: "admin" | "seller";
  analytics: CouponAnalytics;
  isDeleted?: boolean;
  conditions?: CouponConditions;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

// Deal Interfaces
export interface DealTotalUnitsGrabbedUser {
  userId: string;
  date: Date | string;
  discountAmount: number;
}

export interface DealAnalytics {
  totalUnitsSold: number;
  totalUnitsReserved: number;
  totalUnitsAvailable: number;
  totalFeeCollected: number;
  totalRevenue: number;
  totalUnitsGrabbedUser: DealTotalUnitsGrabbedUser[];
}

export interface Deal {
  _id: string;
  dealId: string;
  productId: string;
  ssin: string;
  dealType: "LIGHTNING_DEAL" | "SEVEN_DAY_DEAL";
  discountType: "PERCENT_OFF" | "AMOUNT_OFF";
  discountValue: number;
  originalPrice: number;
  dealPrice: number;
  quantityAvailable: number;
  startDate: Date | string;
  endDate: Date | string;
  dealFee: number;
  eligibility: boolean;
  visibility: boolean;
  status: "SCHEDULED" | "ACTIVE" | "ENDED" | "CANCELLED" | "DRAFT";
  createdBy: "admin" | "seller";
  analytics: DealAnalytics;
  isDeleted?: boolean;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

// Promotion Interfaces
export interface PromotionBuyerPurchase {
  condtionApplied: boolean;
  purchaseCondition?:
    | "BUYER MUST PURCHASE MINIMUM AMOUNT"
    | "BUYER MUST PURCHASE MINIMUM QUANTITY";
  purchaseValue?: number;
}

export interface PromotionBuyerGet {
  discountType: "AMOUNT_OFF" | "PERCENT_OFF";
  discountValue: number;
}

export interface PromotionPercentageOffTiers {
  buyerPurchase: number;
  buyerGets: number;
}

export interface PromotionClaimCode {
  claimCodeOption: "SINGLE_USE" | "GROUP_USE" | "NO_CLAIM_CODE";
  claimCode: string;
}

export interface PromotionConditions {
  buyerPurchase: PromotionBuyerPurchase;
  productsSelected: string | string[];
  buyerGet: PromotionBuyerGet;
  budget: string;
  percentageOffTiers: PromotionPercentageOffTiers[];
  claimCode: PromotionClaimCode;
}

export interface PromotionSchedule {
  startDate: Date | string;
  endDate: Date | string;
  internalDiscraption: string;
}

export interface PromotionUserWhoGrabbed {
  userId: string;
  discountAmount: number;
}

export interface PromotionAnalytics {
  totalRedemptions?: number;
  lastRedemptionDate?: Date | string;
  totalDiscountGiven?: number;
  discountValue?: number;
  totalBuyerGetsFree?: number;
  userwhoesGrabbedThePromotion?: PromotionUserWhoGrabbed[];
  totalbudget?: number;
  budgetLeft?: number;
}

export interface PromotionBuyXGetYBuyerPurchase {
  buyerPurchaseType: "ON_EVEER_ORDER" | "BUYER_MUST_PURCHASE_MINIMUM_QUANTITY";
  buyerPurchaseValue: number;
}

export interface PromotionBuyXGetYBuyerGetTiers {
  buyerPurchaseValue: number;
  buyerGets: number;
}

export interface PromotionBuyXGetYFree {
  buyxgetybuyerPurchase: PromotionBuyXGetYBuyerPurchase;
  productsSelected: string | string[];
  budget: string;
  buyXGetYFreeTiers: PromotionBuyXGetYBuyerGetTiers[];
  claimCode?: PromotionClaimCode;
}

export interface Promotion {
  _id: string;
  promotionId: string;
  sellerId: string;
  createdBy: "admin" | "seller";
  promotionType: "PERCENT_OFF" | "BUY_X_GET_Y_FREE";
  conditionsForPercentageOff?: PromotionConditions;
  conditionsForBuyXGetYFree?: PromotionBuyXGetYFree;
  schedule: PromotionSchedule;
  status: "ACTIVE" | "INACTIVE" | "EXPIRED" | "DRAFT";
  analytics: PromotionAnalytics;
  isDeleted?: boolean;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

// Aura Exclusive Discount Interfaces
export interface AuraDiscountSchedule {
  startDate: Date | string;
  endDate: Date | string;
}

export interface AuraDiscountUsersWhoGetDiscount {
  userId: string;
  discountAmount: number;
}

export interface AuraDiscountTracking {
  totalUnitsSold: number;
  totalUnitsAvailable: number;
  lastUpdated: Date | string;
  totalDiscountGiven: number;
  budgetLeft: number;
  discountAmount: number;
  userswhogetdiscount: AuraDiscountUsersWhoGetDiscount[];
}

export interface AuraDiscountProductDiscount {
  productId: string;
  productSSIN: string;
  discountType: "PERCENT_OFF" | "AMOUNT_OFF";
  discountValue: number;
}

export interface AuraDiscountConditions {
  minimumOrderApply?: boolean;
  minimumQuantity?: number;
}

export interface AuraExclusiveDiscount {
  _id: string;
  discountId: string;
  createrId: string;
  discountName: string;
  schedule: AuraDiscountSchedule;
  quantity: number;
  productDiscount: AuraDiscountProductDiscount;
  maxDiscountBudget: number;
  status: "ACTIVE" | "ENDED" | "DRAFT" | "PAUSED" | "PENDING";
  tracking: AuraDiscountTracking;
  createdBy: "admin" | "seller";
  isDeleted?: boolean;
  condition?: AuraDiscountConditions;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

// Query DTOs
export interface QueryProductDto {
  page?: number;
  limit?: number;
  search?: string;
  condition?: ProductCondition;
  country?: ProductCountry;
  brandId?: string;
  superCategory?: string;
  category?: string;
  subCategory?: string;
  childCategory?: string;
  discountType?: "PERCENT_OFF" | "AMOUNT_OFF";
  minPrice?: number;
  maxPrice?: number;
  minRating?: number;
  maxRating?: number;
  minDiscountValue?: number;
  maxDiscountValue?: number;
  sortBy?: SortBy;
  sortOrder?: SortOrder;
}

export enum ProductSortOption {
  PRICE_ASC = "price_asc",
  PRICE_DESC = "price_desc",
  NEWEST = "newest",
  RATING = "rating",
}

export enum DeliveryDayOption {
  TODAY = "today",
  TOMORROW = "tomorrow",
}

export interface PublicProductQueryDto {
  categoryId?: string;
  page?: number;
  limit?: number;
  search?: string;
  sort?: ProductSortOption;
  minPrice?: number;
  maxPrice?: number;
  brand?: string[];
  deliveryDay?: DeliveryDayOption;
  discount?: number;
  pattern?: string[];
  fabric?: string[];
  occasion?: string[];
  color?: string[];
}

export interface PublicProduct {
  id: string;
  name: string;
  slug: string;
  brand: string;
  shortDescription: string;
  images: string[];
  rating: number;
  reviewCount: number;
  price: {
    current: number;
    mrp: number;
    currency: string;
    discountPercentage: number;
  };
  isBestSeller: boolean;
  inStock: boolean;
}

export interface SponsoredAdSlot {
  impressionId: string;
  campaignId: string;
  advertiserId: string;
  advertiserName: string;
  adType: string;
  pricingModel: string;
  platformType: string;
  productId?: string;
  productDetailId?: string;
  productName?: string;
  trackingToken: string;
  placement: string;
  creative?: {
    headline?: string;
    logoUrl?: string;
    imageUrl?: string;
    ctaText?: string;
    destinationUrl?: string;
    brandName?: string;
    creativeFormat?: "product_collection" | "store_spotlight" | "video";
    customImageUrl?: string;
    videoUrl?: string;
    thumbnailUrl?: string;
    storePages?: { pageTitle: string; pageUrl: string; imageUrl?: string }[];
    featuredProducts?: { productId: string; name: string; image?: string; catalogProductId?: string }[];
  } | null;
  // Full product-card data (adType 'product' only) — same fields an organic result
  // card has, so SponsoredProductCard can render with real price/rating/colors/delivery
  // instead of just the thin ad creative.
  brand?: string;
  finalPrice?: number;
  mrp?: number;
  discountPercent?: number;
  rating?: { average: number; count: number };
  freeDelivery?: boolean;
  fastDelivery?: boolean;
  nextDayDelivery?: boolean;
  colors?: string[];
}

export interface PublicProductResponse {
  success: boolean;
  data: {
    products: PublicProduct[];
    sponsoredSlots?: SponsoredAdSlot[];
    pagination: {
      totalItems: number;
      totalPages: number;
      currentPage: number;
      limit: number;
    };
    filters: {
      brands: string[];
      colors: string[];
      maxPriceAvailable: number;
    };
  };
}

// Grab DTOs
export interface GrabDealDto {
  dealId: string;
  productId?: string;
}

export interface GrabCouponDto {
  couponId: string;
  ssin: string;
  orderquantity: number;
}

export interface GrabPromotionDto {
  promotionId: string;
  userId: string;
  productId: string;
  quantity?: number;
}

export interface GrabAuraDiscountDto {
  auraDiscountId: string;
  quantity?: number;
}

// API Response Types
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ApiResponse<T> {
  statusCode: number;
  message: string;
  data: T;
}

export interface ProductListResponse extends PaginatedResponse<Product> {
  sponsoredSlots?: SponsoredAdSlot[];
}

export interface ProductDetailResponse extends ApiResponse<Product> {}

export interface BrandDetailResponse extends ApiResponse<BrandWithProducts> {}

export interface GrabDealResponse extends ApiResponse<any> {}

export interface GrabCouponResponse extends ApiResponse<any> {}

export interface GrabPromotionResponse extends ApiResponse<any> {}

export interface GrabAuraDiscountResponse extends ApiResponse<any> {}

// Redux State Types
export interface ECommerceProductsState {
  products: Product[];
  currentProduct: Product | null;
  currentBrand: BrandWithProducts | null;
  productsWithCoupons: Product[];
  productsWithDeals: Product[];
  productsWithPromotions: Product[];
  productsWithBuyXGetYPromotions: Product[];
  productsWithSalarAuraExclusive: Product[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  couponsPagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  dealsPagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  promotionsPagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  buyXGetYPromotionsPagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  auraExclusivePagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  publicProducts: PublicProduct[];
  sponsoredSlots: SponsoredAdSlot[];
  publicPagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: {
    brands: string[];
    colors: string[];
    maxPriceAvailable: number;
  };
  loading: {
    products: boolean;
    product: boolean;
    productsWithCoupons: boolean;
    productsWithDeals: boolean;
    productsWithPromotions: boolean;
    productsWithBuyXGetYPromotions: boolean;
    productsWithSalarAuraExclusive: boolean;
    brand: boolean;
    grabDeal: boolean;
    grabCoupon: boolean;
    grabPromotion: boolean;
    grabAuraDiscount: boolean;
    publicProducts: boolean;
  };
  error: string | null;
}

// Action Types
export const ECOMMERCE_PRODUCTS_ACTION_TYPES = {
  FETCH_PRODUCTS_START: "ecommerceProducts/fetchProducts/start",
  FETCH_PRODUCTS_SUCCESS: "ecommerceProducts/fetchProducts/success",
  FETCH_PRODUCTS_FAILURE: "ecommerceProducts/fetchProducts/failure",
  FETCH_PRODUCT_BY_ID_START: "ecommerceProducts/fetchProductById/start",
  FETCH_PRODUCT_BY_ID_SUCCESS: "ecommerceProducts/fetchProductById/success",
  FETCH_PRODUCT_BY_ID_FAILURE: "ecommerceProducts/fetchProductById/failure",
  FETCH_BRAND_BY_ID_START: "ecommerceProducts/fetchBrandById/start",
  FETCH_BRAND_BY_ID_SUCCESS: "ecommerceProducts/fetchBrandById/success",
  FETCH_BRAND_BY_ID_FAILURE: "ecommerceProducts/fetchBrandById/failure",
  FETCH_PUBLIC_PRODUCTS_START: "ecommerceProducts/fetchPublicProducts/start",
  FETCH_PUBLIC_PRODUCTS_SUCCESS: "ecommerceProducts/fetchPublicProducts/success",
  FETCH_PUBLIC_PRODUCTS_FAILURE: "ecommerceProducts/fetchPublicProducts/failure",
  FETCH_PRODUCTS_WITH_COUPONS_START:
    "ecommerceProducts/fetchProductsWithCoupons/start",
  FETCH_PRODUCTS_WITH_COUPONS_SUCCESS:
    "ecommerceProducts/fetchProductsWithCoupons/success",
  FETCH_PRODUCTS_WITH_COUPONS_FAILURE:
    "ecommerceProducts/fetchProductsWithCoupons/failure",
  FETCH_PRODUCTS_WITH_DEALS_START:
    "ecommerceProducts/fetchProductsWithDeals/start",
  FETCH_PRODUCTS_WITH_DEALS_SUCCESS:
    "ecommerceProducts/fetchProductsWithDeals/success",
  FETCH_PRODUCTS_WITH_DEALS_FAILURE:
    "ecommerceProducts/fetchProductsWithDeals/failure",
  FETCH_PRODUCTS_WITH_PROMOTIONS_START:
    "ecommerceProducts/fetchProductsWithPromotions/start",
  FETCH_PRODUCTS_WITH_PROMOTIONS_SUCCESS:
    "ecommerceProducts/fetchProductsWithPromotions/success",
  FETCH_PRODUCTS_WITH_PROMOTIONS_FAILURE:
    "ecommerceProducts/fetchProductsWithPromotions/failure",
  FETCH_PRODUCTS_WITH_BUY_X_GET_Y_PROMOTIONS_START:
    "ecommerceProducts/fetchProductsWithBuyXGetYPromotions/start",
  FETCH_PRODUCTS_WITH_BUY_X_GET_Y_PROMOTIONS_SUCCESS:
    "ecommerceProducts/fetchProductsWithBuyXGetYPromotions/success",
  FETCH_PRODUCTS_WITH_BUY_X_GET_Y_PROMOTIONS_FAILURE:
    "ecommerceProducts/fetchProductsWithBuyXGetYPromotions/failure",
  FETCH_PRODUCTS_WITH_SALARAURA_EXCLUSIVE_START:
    "ecommerceProducts/fetchProductsWithSalarAuraExclusive/start",
  FETCH_PRODUCTS_WITH_SALARAURA_EXCLUSIVE_SUCCESS:
    "ecommerceProducts/fetchProductsWithSalarAuraExclusive/success",
  FETCH_PRODUCTS_WITH_SALARAURA_EXCLUSIVE_FAILURE:
    "ecommerceProducts/fetchProductsWithSalarAuraExclusive/failure",
  GRAB_DEAL_START: "ecommerceProducts/grabDeal/start",
  GRAB_DEAL_SUCCESS: "ecommerceProducts/grabDeal/success",
  GRAB_DEAL_FAILURE: "ecommerceProducts/grabDeal/failure",
  GRAB_COUPON_START: "ecommerceProducts/grabCoupon/start",
  GRAB_COUPON_SUCCESS: "ecommerceProducts/grabCoupon/success",
  GRAB_COUPON_FAILURE: "ecommerceProducts/grabCoupon/failure",
  GRAB_PROMOTION_START: "ecommerceProducts/grabPromotion/start",
  GRAB_PROMOTION_SUCCESS: "ecommerceProducts/grabPromotion/success",
  GRAB_PROMOTION_FAILURE: "ecommerceProducts/grabPromotion/failure",
  GRAB_AURA_DISCOUNT_START: "ecommerceProducts/grabAuraDiscount/start",
  GRAB_AURA_DISCOUNT_SUCCESS: "ecommerceProducts/grabAuraDiscount/success",
  GRAB_AURA_DISCOUNT_FAILURE: "ecommerceProducts/grabAuraDiscount/failure",
  CLEAR_ERROR: "ecommerceProducts/clearError",
  CLEAR_CURRENT_PRODUCT: "ecommerceProducts/clearCurrentProduct",
  CLEAR_CURRENT_BRAND: "ecommerceProducts/clearCurrentBrand",
} as const;
